// @ts-nocheck
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep";

export interface StepformInput {
    isValid: boolean;
    value: string;
    required: boolean;
}

export interface StepFormStep {
    valid: boolean;
    label: string;
    description: string;
    percentComplete: number;
    inputs: Record<string, StepformInput>;
    [key: string]: unknown;
}
export interface StepForm {
    steps: StepFormStep[];
    title: string;
    isFormValid: boolean;
}

export type StepFormState = Record<string, StepForm> & { downloadLink?: string };

const initialState: StepFormState = {};

const emptyForm: StepForm = {
    steps: [],
    title: "",
    isFormValid: false,
};

const handleFormCompletePercentage = (stepForm: StepForm) => {
    const { steps } = stepForm;
    const stepsWithoutReview = steps.slice(0, -1);
    const numberOfSteps = stepsWithoutReview.length;

    const totalPercent = stepsWithoutReview.reduce((acc, step) => {
        return acc + step.percentComplete;
    }, 0);
    const percentageComplete = totalPercent / numberOfSteps;

    stepForm.isFormValid = percentageComplete === 100;
    stepForm.steps[stepForm.steps.length - 1].percentComplete = percentageComplete;
    stepForm.steps[stepForm.steps.length - 1].valid = stepForm.isFormValid;
};

const handleStepPercentageComplete = (step: StepFormStep) => {
    const inputs = Object.values(step.inputs);
    let totalQuestions = 0;
    let incorrectQuestions = 0;

    inputs.forEach(input => {
        if (input.required) {
            totalQuestions += 1;
            if (!input.isValid) {
                incorrectQuestions += 1;
            }
        }
    });
    const isStepValid = totalQuestions === 0 || incorrectQuestions === 0;
    step.valid = isStepValid;
    const percentageComplete = isStepValid ? 100 : ((totalQuestions - incorrectQuestions) / totalQuestions) * 100;
    step.percentComplete = percentageComplete;
};
type AddStepsTuple = [number, StepFormStep];
const actions = {
    "Data/StepForm/initializeStepForm": (
        draft: Draft<StepFormState>,
        action: { formKey: string; steps: StepForm["steps"]; title: string },
    ) => {
        const form = localStorage.getItem(action.formKey);
        if (form) {
            draft[action.formKey] = JSON.parse(form);
        } else {
            draft[action.formKey] = { steps: action.steps, title: action.title, isFormValid: false };
        }
    },
    "Data/StepForm/ClearData": (draft: Draft<StepFormState>, action: { formKey: string }) => {
        draft[action.formKey] = emptyForm;
        localStorage.removeItem(action.formKey);
    },
    "Data/StepForm/SaveDownloadLink": (draft: Draft<StepFormState>, action: { downloadLink: string }) => {
        draft.downloadLink = action.downloadLink;
    },
    "Data/StepForm/SaveFormToLocalStorage": (draft: Draft<StepFormState>, action: { formKey: string }) => {
        const steps = cloneDeep(draft[action.formKey].steps);
        const stepsWithoutBankAccount = steps.map(step => {
            const { inputs, ...rest } = step;
            const inputsWithoutBankAccount = Object.entries(inputs).reduce((acc, [key, value]) => {
                if (key !== "bankAccountNumber") {
                    acc[key] = value;
                    return acc;
                }
                acc[key] = {
                    ...value,
                    value: "",
                    isValid: false,
                };
                return acc;
            }, {} as Record<string, StepformInput>);

            return { ...rest, inputs: inputsWithoutBankAccount };
        });
        const data = { ...draft[action.formKey], steps: stepsWithoutBankAccount };

        localStorage.setItem(action.formKey, JSON.stringify(data));
    },
    "Data/StepForm/DeleteSteps": (draft: Draft<StepFormState>, action: { formKey: string; steps: number[] }) => {
        const newSteps = draft[action.formKey].steps.filter((_, index) => !action.steps.includes(index));

        draft[action.formKey].steps = newSteps;
        handleFormCompletePercentage(draft[action.formKey]);
    },
    "Data/StepForm/AddSteps": (draft: Draft<StepFormState>, action: { formKey: string; steps: AddStepsTuple[] }) => {
        action.steps.forEach(([index, step]) => {
            draft[action.formKey].steps.splice(index, 0, step);
        });
        handleFormCompletePercentage(draft[action.formKey]);
    },
    "Data/StepForm/RemoveInput": (
        draft: Draft<StepFormState>,
        action: { formKey: string; stepIndex: number; inputKey: string },
    ) => {
        const step = draft[action.formKey].steps[action.stepIndex];
        delete step.inputs[action.inputKey];
        handleStepPercentageComplete(step);
        handleFormCompletePercentage(draft[action.formKey]);
    },
    "Data/StepForm/UpdateStepForm": (
        draft: Draft<StepFormState>,
        action: {
            formKey: string;
            inputKey: string;
            value: string;
            stepIndex: number;
            required: boolean;
            isValid: boolean;
        },
    ) => {
        const { formKey, inputKey, value, stepIndex, isValid, required } = action;
        if (!draft[formKey]) {
            draft[formKey] = emptyForm;
        }
        const form = draft[formKey];
        const { steps } = form;
        let currentStep = steps[stepIndex];
        if (!currentStep) {
            steps[stepIndex] = {
                valid: false,
                inputs: {},
                percentComplete: 0,
                label: "",
                description: "",
            };
            currentStep = steps[stepIndex];
        }

        currentStep.inputs[inputKey] = { value, isValid, required };

        handleStepPercentageComplete(currentStep);
        handleFormCompletePercentage(form);
    },
    "Data/StepForm/SetStepFormIsValid": (
        draft: Draft<StepFormState>,
        action: { formKey: string; stepIndex: number; isValid: boolean; percentComplete: number },
    ) => {
        const { formKey, stepIndex, isValid } = action;
        if (!draft[formKey]?.steps[stepIndex]) {
            return;
        }
        draft[formKey].steps[stepIndex].valid = isValid;
        draft[formKey].steps[stepIndex].percentComplete = action.percentComplete;
        handleFormCompletePercentage(draft[formKey]);
    },
    "Data/StepForm/UpdateCustomInputType": (
        draft: Draft<StepFormState>,
        action: { formKey: string; inputKey: string; value: any; stepIndex: number },
    ) => {
        if (action.inputKey === "valid" || action.inputKey === "inputs") {
            throw new Error("Invalid input key");
        }
        if (!draft[action.formKey]) {
            draft[action.formKey] = emptyForm;
        }
        if (!draft[action.formKey].steps[action.stepIndex]) {
            draft[action.formKey].steps[action.stepIndex] = {
                valid: false,
                inputs: {},
                percentComplete: 0,
                label: "",
                description: "",
            };
        }
        draft[action.formKey].steps[action.stepIndex][action.inputKey] = action.value;
    },
};

const reducer = createTypedReducerWithImmer(initialState, actions);
export type StepFormActions = Parameters<typeof reducer>[1];
export default reducer;
